import styled, { css } from "styled-components";
import { checkBubbleAnimation } from "./Check";
import { Icon } from "@introist/react-foundation/v2";
import { ExplodingLines } from "./components/ExplodingLines";

type LargeCheckProps = {
  checked: boolean;
  animate?: boolean;
};

const StyledLargeCheck = styled.div<{ $checked?: boolean; $animate: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 1.5rem;
  position: relative;

  .check {
    position: relative;
    stroke: white;
    z-index: 1;
  }

  ::after {
    display: block;
    position: absolute;
    inset: 0;
    border-radius: inherit;
    width: inherit;
    height: inherit;
    background-color: var(--palette-primary-default);
  }

  ${({ $checked, $animate }) => {
    if ($checked && $animate) {
      return css`
        ::after {
          content: "";
          animation-name: ${checkBubbleAnimation};
          animation-duration: 160ms;
          animation-timing-function: ease-out;
          animation-iteration-count: 1;
          animation-fill-mode: forwards;
        }
      `;
    }

    if ($checked && !$animate) {
      return css`
        ::after {
          content: "";
        }
      `;
    }
  }}
`;

export const LargeCheck = ({ checked, animate }: LargeCheckProps) => {
  return (
    <StyledLargeCheck $checked={checked} $animate={!!animate}>
      {checked ? (
        <Icon name="check" className="check" />
      ) : (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.4493 0.210507C10.9441 0.13114 11.4493 0.0829345 11.9627 0.0678774C12.476 0.0528204 12.9831 0.0713357 13.4818 0.121563C14.1 0.183831 14.5506 0.735453 14.4884 1.35364C14.4261 1.97184 13.8745 2.4225 13.2563 2.36023C12.8542 2.31974 12.4445 2.30471 12.0286 2.31691C11.6128 2.32911 11.2046 2.36812 10.8056 2.43211C10.1921 2.53051 9.61502 2.11295 9.51663 1.49947C9.41823 0.885987 9.83579 0.3089 10.4493 0.210507ZM6.5413 2.83682C6.93417 3.31817 6.86244 4.02686 6.3811 4.41972C5.74616 4.93795 5.18082 5.53745 4.7007 6.20167C4.33672 6.70522 3.63345 6.81836 3.12991 6.45438C2.62636 6.09039 2.51322 5.38713 2.8772 4.88358C3.47189 4.06087 4.17196 3.3185 4.9584 2.67662C5.43974 2.28375 6.14843 2.35547 6.5413 2.83682ZM17.537 2.51432C17.901 2.01077 18.6042 1.89764 19.1078 2.26162C19.9305 2.8563 20.6728 3.55637 21.3147 4.34281C21.7076 4.82416 21.6359 5.53285 21.1545 5.92571C20.6732 6.31858 19.9645 6.24686 19.5716 5.76551C19.0534 5.13057 18.4539 4.56524 17.7897 4.08511C17.2861 3.72113 17.173 3.01786 17.537 2.51432ZM22.4919 8.90104C23.1054 8.80265 23.6824 9.22021 23.7808 9.83369C23.8602 10.3285 23.9084 10.8337 23.9235 11.3471C23.9385 11.8604 23.92 12.3676 23.8698 12.8662C23.8075 13.4844 23.2559 13.9351 22.6377 13.8728C22.0195 13.8105 21.5688 13.2589 21.6311 12.6407C21.6716 12.2386 21.6866 11.8289 21.6744 11.413C21.6622 10.9972 21.6232 10.589 21.5592 10.19C21.4608 9.57652 21.8784 8.99943 22.4919 8.90104ZM1.96923 9.50297C2.58743 9.56524 3.03809 10.1169 2.97582 10.735C2.93532 11.1371 2.9203 11.5469 2.9325 11.9627C2.94469 12.3786 2.98371 12.7867 3.0477 13.1858C3.1461 13.7992 2.72853 14.3763 2.11505 14.4747C1.50157 14.5731 0.924487 14.1556 0.826094 13.5421C0.746728 13.0472 0.698522 12.5421 0.683465 12.0287C0.668408 11.5153 0.686924 11.0082 0.737151 10.5096C0.799419 9.89137 1.35104 9.4407 1.96923 9.50297ZM3.45241 17.45C3.93375 17.0572 4.64244 17.1289 5.03531 17.6102C5.55354 18.2452 6.15304 18.8105 6.81726 19.2906C7.32081 19.6546 7.43394 20.3579 7.06996 20.8614C6.70598 21.365 6.00271 21.4781 5.49917 21.1141C4.67646 20.5195 3.93409 19.8194 3.2922 19.0329C2.89934 18.5516 2.97106 17.8429 3.45241 17.45ZM21.477 16.9214C21.9806 17.2854 22.0937 17.9886 21.7297 18.4922C21.135 19.3149 20.435 20.0573 19.6485 20.6991C19.1672 21.092 18.4585 21.0203 18.0656 20.5389C17.6728 20.0576 17.7445 19.3489 18.2258 18.956C18.8608 18.4378 19.4261 17.8383 19.9062 17.1741C20.2702 16.6705 20.9735 16.5574 21.477 16.9214ZM10.1186 22.0221C10.1808 21.4039 10.7324 20.9533 11.3506 21.0155C11.7527 21.056 12.1625 21.071 12.5783 21.0589C12.9942 21.0467 13.4023 21.0076 13.8013 20.9436C14.4148 20.8453 14.9919 21.2628 15.0903 21.8763C15.1887 22.4898 14.7711 23.0669 14.1577 23.1653C13.6628 23.2446 13.1576 23.2928 12.6443 23.3079C12.1309 23.3229 11.6238 23.3044 11.1251 23.2542C10.507 23.1919 10.0563 22.6403 10.1186 22.0221Z"
            fill="#BBC1C7"
          />
        </svg>
      )}
      {checked && animate && <ExplodingLines color="#0E181D" />}
    </StyledLargeCheck>
  );
};
